/**
 * Temporary Order Service
 * Handles retrieval and management of temporary orders created for quote requests
 */

// API Base URL - configurable for different environments
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api/v3';

export interface TemporaryOrder {
  id: string;
  order_type: string;
  supplier_id: string;
  order_date: string;
  due_date: string;
  notes: string;
  status: string;
  case_id: string;
  created_at: string;
  updated_at: string;
  positions: TemporaryOrderPosition[];
  files?: PositionFile[];
}

export interface TemporaryOrderPosition {
  id: string;
  order_id: string;
  position_number: number;
  quantity: number;
  part_name: string;
  part_number: string;
  notes: string;
  type_specific_data: Record<string, any>;
  delivery_status: string;
  file_count: number;
  files?: PositionFile[];
}

export interface PositionFile {
  id: string;
  position_id: string;
  order_id: string;
  filename: string;
  original_filename: string;
  file_path: string;
  file_size: number;
  file_type: string;
  mime_type: string;
  description: string;
  uploaded_at: string;
  uploaded_by: string;
}

export interface QuoteRequestEmailData {
  to: string[];
  subject: string;
  body_text: string;
  attachments: PositionFile[];
  case_id: string;
  order_data: TemporaryOrder;
}

class TemporaryOrderService {
  private baseUrl = API_BASE_URL;

  /**
   * Helper method for API calls
   */
  private async apiCall(endpoint: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET', data?: any): Promise<any> {
    try {
      const config: RequestInit = {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
      };

      if (data && method !== 'GET') {
        config.body = JSON.stringify(data);
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, config);
      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'API call failed',
          error: result.error
        };
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Retrieve temporary order by case ID
   */
  async getTemporaryOrderByCase(caseId: string): Promise<{ success: boolean; data?: TemporaryOrder; error?: string }> {
    try {
      const response = await this.apiCall(`/cases/${caseId}/temporary-order`);
      return response;
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Retrieve temporary order directly by order ID
   */
  async getTemporaryOrder(orderId: string): Promise<{ success: boolean; data?: TemporaryOrder; error?: string }> {
    try {
      // Get order details
      const orderResponse = await this.apiCall(`/orders/${orderId}`);
      if (!orderResponse.success) {
        return orderResponse;
      }

      // Get positions
      const positionsResponse = await this.apiCall(`/orders/${orderId}/positions`);
      if (!positionsResponse.success) {
        return positionsResponse;
      }

      // Get files for each position
      const positions = positionsResponse.data || [];
      for (const position of positions) {
        const filesResponse = await this.apiCall(`/orders/${orderId}/positions/${position.id}/files`);
        if (filesResponse.success) {
          position.files = filesResponse.data || [];
        }
      }

      const temporaryOrder: TemporaryOrder = {
        ...orderResponse.data,
        positions
      };

      return { success: true, data: temporaryOrder };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Generate quote request email content from temporary order
   */
  async generateQuoteRequestEmail(caseId: string): Promise<{ success: boolean; data?: QuoteRequestEmailData; error?: string }> {
    try {
      const response = await this.apiCall(`/cases/${caseId}/generate-quote-email`, 'POST');
      return response;
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Send quote request email with attachments
   */
  async sendQuoteRequestEmail(caseId: string, emailData?: Partial<QuoteRequestEmailData>): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      // Format the request body to match backend expectations
      const requestBody = emailData ? {
        email_content: emailData,
        supplier_email: Array.isArray(emailData.to) ? emailData.to[0] : emailData.to,
        send_immediately: true
      } : undefined;
      
      const response = await this.apiCall(`/cases/${caseId}/send-quote-email`, 'POST', requestBody);
      return response;
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get files for a specific position
   */
  async getPositionFiles(orderId: string, positionId: string): Promise<{ success: boolean; data?: PositionFile[]; error?: string }> {
    try {
      const response = await this.apiCall(`/orders/${orderId}/positions/${positionId}/files`);
      return response;
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Upload files to a position
   */
  async uploadPositionFiles(orderId: string, positionId: string, files: File[], description?: string): Promise<{ success: boolean; data?: PositionFile[]; error?: string }> {
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      if (description) {
        formData.append('description', description);
      }

      const response = await fetch(`${this.baseUrl}/orders/${orderId}/positions/${positionId}/files`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();
      return result;
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get file download URL
   */
  getFileDownloadUrl(filePath: string): string {
    const cleanPath = filePath.startsWith('storage/')
      ? filePath.substring('storage/'.length)
      : filePath;
    return `${this.baseUrl}/files/${cleanPath}`;
  }

  /**
   * Format order type for display
   */
  formatOrderType(orderType: string): string {
    const types: Record<string, string> = {
      'material': 'Material',
      'heat_treatment': 'Heat Treatment',
      'coating': 'Coating',
      'carbide': 'Carbide',
      'tool': 'Tool',
      'machining': 'Machining',
      'assembly': 'Assembly'
    };
    return types[orderType] || orderType;
  }

  /**
   * Format type-specific data for display
   */
  formatTypeSpecificData(data: Record<string, any>, orderType: string): Array<{ label: string; value: string }> {
    const formatted: Array<{ label: string; value: string }> = [];
    
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        formatted.push({
          label,
          value: String(value)
        });
      }
    });

    return formatted;
  }

  /**
   * Calculate total files count for order
   */
  getTotalFilesCount(order: TemporaryOrder): number {
    return order.positions.reduce((total, position) => {
      return total + (position.files?.length || 0);
    }, 0);
  }

  /**
   * Get all files from all positions
   */
  getAllOrderFiles(order: TemporaryOrder): PositionFile[] {
    const allFiles: PositionFile[] = [];
    order.positions.forEach(position => {
      if (position.files) {
        allFiles.push(...position.files);
      }
    });
    return allFiles;
  }
}

export const temporaryOrderService = new TemporaryOrderService();
export default temporaryOrderService;
